/*
 * Promotion countdown for the announcement bar.
 *
 * Mirrors the live theme's `useCountdown` (src/sections/AnnouncementBar.tsx):
 * it counts down once a second in HH:MM:SS and, on reaching zero, starts the
 * window again rather than sitting at 00:00:00.
 *
 * The one departure: the live timer restarts from the top on every page view,
 * so a reload resets it. With "Mantener por visitante" on — the default — the
 * deadline is stored per visitor, so it keeps running across pages and reloads
 * and only rolls over when the window genuinely elapses.
 */
(() => {
  const pad = (value) => String(value).padStart(2, '0');

  const format = (seconds) => {
    const safe = Math.max(0, seconds);
    return `${pad(Math.floor(safe / 3600))}:${pad(Math.floor((safe % 3600) / 60))}:${pad(safe % 60)}`;
  };

  const store = {
    read(key) {
      try { return Number(window.localStorage.getItem(key)) || 0; } catch { return 0; }
    },
    write(key, value) {
      try { window.localStorage.setItem(key, String(value)); } catch { /* private mode */ }
    },
  };

  // One deadline per countdown, shared by the marquee's duplicated copy.
  const deadlineFor = (element, window_ms) => {
    const key = `wowthings:countdown:${element.dataset.key}`;
    if (element.dataset.persist !== 'true') return Date.now() + window_ms;

    let deadline = store.read(key);
    // Roll forward in whole windows so a visitor who returns days later sees a
    // fresh one instead of a negative number.
    if (!deadline || deadline <= Date.now()) {
      const elapsed = deadline ? Date.now() - deadline : 0;
      deadline = Date.now() + window_ms - (elapsed % window_ms);
      store.write(key, deadline);
    }
    return deadline;
  };

  const timers = new Map();

  const start = (element) => {
    if (timers.has(element)) return;
    const minutes = Number(element.dataset.minutes);
    if (!Number.isFinite(minutes) || minutes <= 0) return;
    const window_ms = minutes * 60 * 1000;
    let deadline = deadlineFor(element, window_ms);

    const tick = () => {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        deadline = Date.now() + window_ms;
        if (element.dataset.persist === 'true') {
          store.write(`wowthings:countdown:${element.dataset.key}`, deadline);
        }
      }
      const seconds = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      const text = format(seconds);
      // The marquee renders the track twice; both copies show the same clock.
      document.querySelectorAll(`[data-announcement-countdown][data-key="${element.dataset.key}"]`)
        .forEach((node) => { node.textContent = text; });
      element.setAttribute('datetime', `PT${seconds}S`);
    };

    tick();
    timers.set(element, window.setInterval(tick, 1000));
  };

  const release = () => {
    timers.forEach((id, element) => {
      if (element.isConnected) return;
      window.clearInterval(id);
      timers.delete(element);
    });
  };

  const initialize = (scope) => {
    release();
    // Only the first copy of each key drives the clock; it writes to both.
    const seen = new Set();
    scope.querySelectorAll('[data-announcement-countdown]').forEach((element) => {
      if (seen.has(element.dataset.key)) return;
      seen.add(element.dataset.key);
      start(element);
    });
  };

  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
